// Create / edit request logic for the Events tab (mirrors screens/AdminScreen.js
// handleCreateEvent :959 and handleEditEvent :1164). Pure async functions: they
// throw Error(message) for validation/service failures; callers show the message.
import { collection, doc } from "firebase/firestore";
import { db, COLLECTIONS } from "../../../firebase.web";
import { parseEventDateTime } from "../../../../../utils/eventFormDateTime";
import { EventService } from "../../../../../services/eventService";
import { UploadService } from "../../../../../services/uploadService";
import { NotificationService, buildEventNotificationData } from "../../../../../services/notificationService";
import { releaseUri } from "../../../lib/files";
import { signedInAs } from "../../../lib/session";
import { isoDateToForm, isoTimeToForm, msg } from "./eventUtils";

const fail = (m) => { throw new Error(m); };

function readForm(form) {
  const title = form.title?.trim();
  const location = form.location?.trim();
  if (!title || !form.date || !form.time || !location) {
    fail("Please fill in all required fields (Title, Date, Time, Location)");
  }
  // Same strings the mobile form holds, validated by the same parser.
  const start = parseEventDateTime(isoDateToForm(form.date), isoTimeToForm(form.time));
  if (!start) fail("Choose a valid event date and time.");
  return { title, location, description: form.description?.trim() || "", start };
}

const SWITCHED = "You were signed out or switched accounts. Nothing was saved.";
const isRemote = (uri) => typeof uri === "string" && uri.startsWith("http");

/** Returns `{ events, extra }`; `partial` events ride on the thrown error's `.created`. */
export async function createEvent({ form, club, uid }) {
  if (!uid) fail("User not authenticated. Please log in again.");
  const { title, location, description, start } = readForm(form);
  const clubId = club.id;
  const pending = form.image_url;

  if (form.is_recurring) {
    const occurrences = parseInt(form.recurrence_count, 10) || 0;
    if (occurrences < 2 || occurrences > 52) fail("Number of occurrences must be between 2 and 52");
    let imageUrl = null;
    if (pending) {
      imageUrl = isRemote(pending) ? pending : await UploadService.uploadEventImage(pending, doc(collection(db, COLLECTIONS.EVENTS)).id);
    }
    const base = {
      club_id: clubId,
      title,
      description,
      location,
      is_public: form.is_public,
      qr_checkin_enabled: true,
      ...(imageUrl ? { image_url: imageUrl } : {}),
    };
    if (!signedInAs(uid)) fail(SWITCHED);
    const { data: created, error } = await EventService.createRecurringEvents(base, uid, {
      firstStartDate: start,
      frequency: form.recurrence_frequency,
      occurrences,
    });
    if (error) {
      const e = new Error(msg(error, "Failed to create events"));
      e.created = created || [];
      throw e;
    }
    // One notification for the whole series, deep-linking to the first occurrence. Skipped if the
    // browser switched accounts after the write (the fan-out would carry the other account's sender_id).
    if (signedInAs(uid)) NotificationService.sendClubNotification(clubId, {
      title: "New Recurring Event",
      message: `${title} - ${location} (${created.length} occurrences)`,
      data: buildEventNotificationData(created[0].id, { clubId, type: "new_event" }),
    }).catch(() => {});
    return { events: created, extra: created.length - 1 };
  }

  let eventId = null;
  let imageUrl = null;
  if (pending) {
    if (isRemote(pending)) imageUrl = pending;
    else {
      eventId = doc(collection(db, COLLECTIONS.EVENTS)).id;
      imageUrl = await UploadService.uploadEventImage(pending, eventId);
    }
  }
  const data = {
    club_id: clubId,
    title,
    description,
    start_date: start.toISOString(),
    location,
    is_public: form.is_public,
    qr_checkin_enabled: true,
    ...(imageUrl ? { image_url: imageUrl } : {}),
  };
  if (!signedInAs(uid)) fail(SWITCHED);
  const { data: created, error } = await EventService.createEvent(data, uid, eventId ? { eventId } : undefined);
  if (error) fail(msg(error, "Failed to create event"));
  if (signedInAs(uid)) NotificationService.sendClubNotification(clubId, {
    title: "New Event Posted",
    message: `${title} - ${location}`,
    data: buildEventNotificationData(created.id, { clubId, type: "new_event" }),
  }).catch(() => {});
  return { events: [created], extra: 0 };
}

/**
 * Edit. Returns `{ patch, imageUpload }`: `patch` is what was written; `imageUpload`
 * (when a new local photo was picked) is an async fn that uploads it and writes
 * `{ image_url }` in a second updateEvent, resolving to the final patch.
 */
export async function editEvent({ form, event, uid }) {
  const { title, location, start } = readForm(form);
  const needsUpload = form.image_url && !isRemote(form.image_url);
  const removed = !form.image_url && event.image_url;
  let immediate;
  if (removed) immediate = "";
  else if (!needsUpload) immediate = form.image_url || event.image_url || "";

  const update = {
    title: form.title,
    description: form.description,
    start_date: start.toISOString(),
    end_date: null, // End dates were removed 2026-08-19: clear any legacy value on every edit.
    location: form.location,
    is_public: form.is_public,
    qr_checkin_enabled: true,
  };
  if (!needsUpload && immediate !== event.image_url) update.image_url = immediate || null;

  if (!signedInAs(uid)) fail(SWITCHED);
  const { error } = await EventService.updateEvent(event.id, update, uid);
  if (error) fail("Failed to update event");

  const pendingUri = needsUpload ? form.image_url : null;
  const imageUpload = pendingUri
    ? async () => {
        const sameAccount = () => signedInAs(uid); // the account that started the edit
        try {
          if (!sameAccount()) return null; // account changed: never write with another account's token
          const imageUrl = await UploadService.uploadEventImage(pendingUri, event.id);
          if (!sameAccount()) return null;
          const { error: e2 } = await EventService.updateEvent(event.id, { image_url: imageUrl }, uid);
          if (e2) throw new Error(msg(e2, "Could not attach the photo"));
          return { image_url: imageUrl };
        } finally {
          releaseUri(pendingUri);
        }
      }
    : null;
  return { patch: { ...update, title, location }, imageUpload };
}
